import React, { useState, useEffect } from 'react';
import finalLogo from '../assets/images/final-logo.svg';
import forgotPassBg from '../assets/images/forgot-password.webp';
import Button from '../components/shared/Button';
import { UserService } from '../services';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import emailSentSuccess from '../assets/icons/emailSentSuccess.svg';
import Modal from '../components/modals/modal';
import { Toast } from '../components/shared/Toast';

const styles = {
	section: 'flex flex-col lg:flex-row min-h-screen bg-bgColor',
	leftColumn:
		'w-full lg:w-1/2 flex items-center justify-center py-8 px-6 xl:pr-[243px] xl:pl-[150px]',
	imageStyle: 'h-12 w-[260px] mb-20 mx-auto lg:mx-0',
	mainHeading: 'text-left text-main leading-39 font-semibold text-dark-blue mb-3',
	subHeading: 'text-left text-lg leading-100 text-gray mb-[50px] font-medium',
	emailLabel: 'block text-base leading-100 font-medium text-dark-blue mb-3',
	emailInput: 'w-full pl-3.5 py-3.5 border-1p3 border-lightGrey rounded-10p placeholder-gray-400',
	rightBgImage: 'hidden lg:block relative w-1/2 bg-cover bg-center',
	carouselText: 'text-carousel leading-130 font-semibold text-white'
};

const ForgotPasswordPage = () => {
	const navigate = useNavigate();
	const [email, setEmail] = useState('');
	const [currentTextIndex, setCurrentTextIndex] = useState(0);
	const [isImageLoaded, setIsImageLoaded] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [showEmailSentModal, setShowEmailSentModal] = useState(false);
	const [error, setError] = useState('');
	// const [modalDescription, setModalDescription] = useState('');

	const carouselTexts = [
		'From your first word to the final format, WriteForm helps you craft clean, structured, and impactful documents — without the usual formatting struggle.',
		'Bring clarity to your content — create, format, and finish documents that are ready to impress.',
		'Save time and reduce stress — WriteForm delivers accurate, consistent formatting so your papers are submission ready.',
		'Access your documents anywhere, on any device — securely stored in the cloud.'
	];

	useEffect(() => {
		const interval = setInterval(() => {
			setCurrentTextIndex((prevIndex) => (prevIndex + 1) % carouselTexts.length);
		}, 5000); // Change text every 5 seconds

		return () => clearInterval(interval);
	}, [carouselTexts.length]);

	useEffect(() => {
		const img = new Image();
		img.src = forgotPassBg;
		img.onload = () => setIsImageLoaded(true);
	}, []);

	const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

	const handleSubmit = async (e) => {
		e.preventDefault();
		//setError('');

		const trimmedEmail = email.trim();
		if (!isValidEmail(trimmedEmail)) {
			Toast.error('Please enter a valid email address (e.g.,name@email.com).');
			return;
		}

		setIsLoading(true);
		try {
			const response = await UserService.requestPasswordReset(trimmedEmail);
			if (response.status === 200) {
				setShowEmailSentModal(true);
				setIsLoading(false);
			}
			else if (response.status === 401) {
				Toast.error(response.message);
				setIsLoading(false);
			} else if (response.status === 404) {
				Toast.error(response.message || "No account found with this email. Please sign up instead.");
				setIsLoading(false);
			} else if (response.status === 400) {
				// Backend validation error
				Toast.error(response.message || 'Please enter a valid email address (e.g.,name@email.com).');
				setIsLoading(false);
			}
		} catch (error) {
			console.error('Request failed with error: ', error);
			Toast.error('Network error. Please check your connection and try again.');
			// avoid email enumeration regardless of outcome
		}
        finally {
			// Toast.success('If an account exists, we sent a code');
			setIsLoading(false);
		}
	};
	const navigateToCodeScreen = () => {
		navigate('/verify-email', { state: { email, mode: 'forgot-password' }, replace: true });
	};

	return (
		<div className={styles.section}>
			{/* Left Column (Email Verify Form) */}
			<div className={styles.leftColumn}>
				<div className="w-full max-w-md lg:w-[520px] lg:h-[645px]">
					<div className="mb-20">
						<svg
							className="cursor-pointer"
							width="40"
							height="40"
							viewBox="0 0 40 40"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							onClick={() => navigate('/login')}
						>
							<path
								d="M19.0625 31.25L7.8125 20L19.0625 8.75M9.375 20H32.1875"
								stroke="#180A63"
								strokeWidth="3.75"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</div>
					<div>
						<img className={styles.imageStyle} src={finalLogo} alt="WriteForm Logo" />
						<h2 className={styles.mainHeading}>Forgot your password?</h2>
						<p className={styles.subHeading}>No worries — enter your email to reset it.</p>
					</div>
					<form className="" onSubmit={handleSubmit}>
						{/* Using toast notifications for errors; inline banner removed */}
						<div className="mb-[60px]">
							<label htmlFor="email-address" className={styles.emailLabel}>
								Email Address <span className="text-red-500">*</span>
							</label>
							<input
								id="email-address"
								name="email"
								type="email"
								autoComplete="email"
								pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
								required
								className={styles.emailInput}
								placeholder="Email Address *"
								value={email}
								onChange={(e) => {
									const value = e.target.value;
									setEmail(value);
									if (error && isValidEmail(value.trim())) {
										setError('');
									}
								}}
							/>
						</div>

						<div>
							<Button type="submit" disabled={isLoading || !isValidEmail(email.trim())}>{isLoading ? 'Verifying...' : 'Reset Password'}</Button>
						</div>
					</form>
				</div>
			</div>

			{/* Right Column (Image and Text) */}
			<div
				className={styles.rightBgImage}
				style={isImageLoaded ? { backgroundImage: `url(${forgotPassBg})` } : {}}
			>
				{!isImageLoaded ? (
					<div className="absolute inset-0">
						<Skeleton
							height="100%"
							width="100%"
							baseColor="#E5E7EB"
							highlightColor="#F3F4F6"
							duration={1.2}
						/>
					</div>
				) : (
					<div className="absolute inset-0 bg-opacity-40 flex flex-col justify-end p-8">
						<div className="text-white bg-white/5 rounded-lg p-6 mx-4 backdrop-blur-30 border-2 border-white/10">
							<div className="flex justify-start space-x-2 mb-4">
								{carouselTexts.map((_, index) => (
									<span
										key={index}
										className={`transition-all duration-300 ${
											index === currentTextIndex
												? 'h-2 w-8 bg-white rounded-full'
												: 'h-2 w-2 bg-white opacity-50 rounded-full'
										}`}
									></span>
								))}
							</div>
							<p className={styles.carouselText}>{carouselTexts[currentTextIndex]}</p>
						</div>
					</div>
				)}
			</div>
			<Modal
				icon={<img src={emailSentSuccess} alt="Email Sent" className="mx-auto" />}
				isOpen={showEmailSentModal}
				heading={'Code Sent Successfully'}
				description={'Password reset code has been sent to your email.'}
				buttonText={'Got it'}
				onButtonClick={navigateToCodeScreen}
			/>
		</div>
	);
};

export default ForgotPasswordPage;
